import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import {
  AlertTriangle,
  Calendar,
  Crown,
  Gift,
  Gem,
  RotateCw,
  Ticket,
  Trophy,
  Users
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { eventApi } from '../../../services'

const IS_EVENT_ACTIVE = true

const EventPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [eventData, setEventData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!IS_EVENT_ACTIVE) {
      navigate('/', { replace: true })
    }
  }, [navigate])

  useEffect(() => {
    if (!IS_EVENT_ACTIVE) {
      setIsLoading(false)
      return
    }

    let isMounted = true

    const loadEvent = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const data = await eventApi.getEventData()

        if (data?.error) {
          const backendErrorText = t('event.major.errorBackend', 'Ошибка получения данных события')
          toast.error(backendErrorText)
          if (isMounted) {
            setError(backendErrorText)
          }
          return
        }

        if (isMounted) {
          setEventData(data || {})
        }
      } catch (e) {
        if (isMounted) {
          setError(t('event.major.errorLoad', 'Не удалось загрузить событие'))
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadEvent()

    return () => {
      isMounted = false
    }
  }, [t])

  const normalizedEventData = useMemo(() => {
    if (!eventData || typeof eventData !== 'object') {
      return {}
    }

    if (eventData.top || eventData.prize || typeof eventData.you === 'boolean') {
      return eventData
    }

    if (eventData.event && typeof eventData.event === 'object') {
      return eventData.event
    }

    if (eventData.event_ref && typeof eventData.event_ref === 'object') {
      return eventData.event_ref
    }

    if (eventData.data && typeof eventData.data === 'object') {
      return eventData.data
    }

    return eventData
  }, [eventData])

  const topList = useMemo(() => {
    const topData = normalizedEventData?.top
    if (!topData || typeof topData !== 'object' || Array.isArray(topData)) {
      return []
    }

    return Object.entries(topData)
      .map(([slug, entry]) => ({
        slug,
        ...entry
      }))
      .sort((a, b) => Number(a?.position || 999) - Number(b?.position || 999))
  }, [normalizedEventData])

  const prizeList = useMemo(() => (Array.isArray(normalizedEventData?.prize) ? normalizedEventData.prize : []), [normalizedEventData])
  const isUserParticipating = Boolean(normalizedEventData?.you)

  const formatPrice = (value) => Number(value || 0).toLocaleString('ru-RU')

  const truncateTeamName = (value) => {
    const text = String(value || '').trim()
    if (!text) return '—'
    return text.length > 16 ? `${text.slice(0, 16)}…` : text
  }

  const openCaseBySlug = (slug) => {
    navigate(`/cases/${slug}`)
  }

  if (!IS_EVENT_ACTIVE) {
    return null
  }

  if (isLoading) {
    return (
      <div className="min-h-screen px-6 pb-10 pt-6 text-white">
        <div className="mx-auto w-full lg:max-w-6xl">
          <div className="rounded-3xl border border-white/10 bg-surface/70 p-6">
            <div className="flex items-center justify-center">
              <div className="h-10 w-10 rounded-full border-2 border-sky-300/30 border-t-sky-300 animate-spin" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen px-6 pb-10 pt-6 text-white">
        <div className="mx-auto w-full lg:max-w-6xl">
          <div className="rounded-3xl border border-red-500/30 bg-red-500/10 p-6 text-center">
            <div className="flex items-center justify-center">
              <AlertTriangle size={28} className="text-red-200" />
            </div>
            <div className="mt-3 text-sm leading-relaxed text-red-100/90">{error}</div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl border border-red-400/40 px-4 py-2 text-sm font-semibold text-red-100"
            >
              <RotateCw size={16} className="text-red-100" />
              {t('event.major.retry', 'Повторить')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen px-6 pb-10 pt-6 text-white">
      <div className="mx-auto w-full lg:max-w-6xl">
        <div className="lg:grid lg:grid-cols-12 lg:items-start lg:gap-6">
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-indigo-300/20 bg-gradient-to-br from-sky-500/10 via-indigo-500/10 to-purple-500/10 overflow-hidden">
              <div className="relative">
                <img src="/images/event/event-major-poster.jpg" alt="event" className="w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070912]/90 via-[#070912]/30 to-transparent" />
              </div>

              <div className="p-5 sm:p-6">
                <h1 className="mt-3 text-3xl font-black leading-tight">
                  {t('event.major.title', 'Major Cologne: Командная Заруба')}
                </h1>

                <p className="mt-3 text-base text-white/85 leading-relaxed">
                  {t('event.major.description', 'Открывай кейсы любимых команд, поднимай их в топ и забирай шанс на скины до $3000!')}
                </p>

                <div className="mt-4 inline-flex items-center gap-2 rounded-xl border border-white/10 bg-dark-primary/45 px-3 py-2 text-sm text-white/85">
                  <Calendar size={14} className="text-purple-200" />
                  {t('event.major.period', '2 июня — 21 июня')}
                </div>

                {isUserParticipating ? (
                  <div className="mt-5 inline-flex items-center gap-2 rounded-xl border border-emerald-300/35 bg-emerald-500/15 px-3 py-2 text-sm font-semibold text-emerald-100">
                    <Crown size={14} className="text-emerald-200" />
                    {t('event.major.iParticipate', 'Я участвую')}
                  </div>
                ) : (
                  <div className="mt-5 inline-flex items-center gap-2 rounded-xl border border-amber-300/35 bg-amber-500/15 px-3 py-2 text-sm font-semibold text-amber-100">
                    <Ticket size={14} className="text-amber-200" />
                    {t('event.major.participationHint', 'Откройте хотя бы 1 кейс чтобы участвовать')}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-4 lg:col-span-7 lg:mt-0">
            <div className="rounded-2xl border border-white/10 bg-surface/70 p-4 sm:p-5">
              <div className="flex items-center gap-2">
                <Trophy size={18} className="text-amber-200" />
                <h2 className="text-lg font-black">{t('event.major.topTitle', 'Лидерборд команд')}</h2>
              </div>

              {topList.length === 0 ? (
                <div className="mt-5 flex items-center justify-center py-6">
                  <Users size={36} className="text-white/35" />
                </div>
              ) : (
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-2">
                  {topList.map((entry) => {
                    const position = Number(entry?.position || 0)
                    const isTopThree = position > 0 && position <= 3

                    return (
                      <button
                        type="button"
                        key={entry.slug}
                        onClick={() => openCaseBySlug(entry.slug)}
                        className={`w-full rounded-xl border p-3.5 text-left transition-colors ${
                          isTopThree
                            ? 'border-amber-300/50 bg-gradient-to-r from-amber-500/20 to-orange-500/20'
                            : 'border-white/10 bg-dark-primary/40 hover:border-sky-300/40'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white/90">
                            {position}
                          </div>

                          <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-white/10 bg-dark-primary">
                            <img src={entry?.img} alt={entry?.name || 'Team'} className="h-full w-full object-cover" />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-semibold text-white">{truncateTeamName(entry?.name)}</div>
                            {Number(entry?.your_count_open || 0) >= 1 && (
                              <div className="mt-0.5 text-xs text-white/65">
                                {t('event.major.myTicketsLabel', 'Мои билеты')}: {Number(entry?.your_count_open || 0)}
                              </div>
                            )}
                          </div>

                          {isTopThree && <Crown size={16} className="shrink-0 text-amber-200" />}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-white/10 bg-surface/70 p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <Gift size={18} className="text-purple-200" />
            <h2 className="text-lg font-black">{t('event.major.prizesTitle', 'Призы ивента')}</h2>
          </div>

          <p className="mt-3 text-sm text-white/80 leading-relaxed">
            {t('event.major.prizesDescription', 'На топ-3 командных кейса будет разыграно по 10 скинов на каждый, всего 30 призов.')}
          </p>

          <div className="mt-4 grid grid-cols-2 gap-2.5 lg:grid-cols-5">
            {prizeList.map((prize, index) => (
              <div key={`${prize?.name || 'prize'}-${index}`} className="rounded-lg border border-white/10 bg-dark-primary/45 p-2">
                <div className="flex justify-center rounded-md bg-black/20 p-1.5">
                  <img
                    src={prize?.img}
                    alt={prize?.name || 'prize'}
                    className="h-[78px] w-[78px] rounded-md object-contain sm:h-[90px] sm:w-[90px]"
                  />
                </div>
                <div className="mt-1.5 text-xs font-semibold leading-tight text-white break-words">{prize?.name}</div>
                <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-white/70">
                  <Gem size={11} className="text-white/60" />
                  {formatPrice(prize?.price)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-surface/70 p-5">
            <div className="flex items-center gap-2">
              <Gift size={18} className="text-sky-200" />
              <h3 className="text-base font-black">{t('event.major.aboutTitle', 'О событии')}</h3>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-white/80 leading-relaxed list-disc pl-5">
              <li>{t('event.major.about1', 'В честь Major Cologne запускаем командный ивент на 3 недели.')}</li>
              <li>{t('event.major.about2', 'Игроки открывают кейсы любимых команд и вместе двигают их вверх в топе.')}</li>
              <li>{t('event.major.about3', 'Каждое открытие влияет на позицию команды в лидерборде.')}</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-white/10 bg-surface/70 p-5">
            <div className="flex items-center gap-2">
              <Users size={18} className="text-indigo-200" />
              <h3 className="text-base font-black">{t('event.major.howToTitle', 'Как участвовать')}</h3>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-white/80 leading-relaxed list-disc pl-5">
              <li>{t('event.major.howTo1', 'Открывай командные кейсы во время ивента.')}</li>
              <li>{t('event.major.howTo2', 'Каждое открытие = 1 билет за соответствующую команду.')}</li>
              <li>{t('event.major.howTo3', 'Учитывается обычное открытие и открытие в кейс-баттле.')}</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-white/10 bg-surface/70 p-5">
            <div className="flex items-center gap-2">
              <Ticket size={18} className="text-amber-200" />
              <h3 className="text-base font-black">{t('event.major.rulesTitle', 'Как выбираются победители')}</h3>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-white/80 leading-relaxed list-disc pl-5">
              <li>{t('event.major.rule1', 'Побеждают 3 команды, чьи кейсы открывали чаще всего за время ивента.')}</li>
              <li>{t('event.major.rule2', 'Среди участников этих команд проводится розыгрыш после окончания ивента.')}</li>
            </ul>
          </div>
        </div>

        <div className="hidden mt-6 rounded-2xl border border-sky-300/25 bg-gradient-to-r from-sky-500/15 to-purple-500/15 p-5 text-center">
          <div className="text-base font-semibold text-white/90">
            {t('event.major.bottomCtaTitle', 'Открывай кейсы команд и продвигай фаворитов в топ!')}
          </div>

          <button
            type="button"
            onClick={() => navigate('/cases')}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-sky-200/40 bg-gradient-to-r from-sky-500/30 to-purple-500/30 px-4 py-3 text-sm font-black text-white hover:border-sky-200/70"
          >
            <Users size={18} className="text-white " />
            {t('event.major.ctaOpenCases', 'Открыть командные кейсы')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EventPage
